import { TWidget } from 'widgets/Widget';

/**
 * @description Core ProductStickyPanel implementation
 * @param Widget Base widget for extending
 * @returns Sticky panel widget
 */
function CoreProductStickyPanelClassCreator(Widget: TWidget) {
    /**
     * @class CoreProductStickyPanel
     * @augments Widget
     * @classdesc Represents product sticky panel component
     * Allows user adds a product to the cart from the sticky panel in case the user has scrolled down and
     * the main "Add to Cart" button is not in the user's viewport.
     * @property {string} data-widget - Widget name `productStickyPanel`
     */
    class CoreProductStickyPanel extends Widget {
        backToTopTimeout?: ReturnType<typeof setTimeout>;

        prefs() {
            return {
                classesOpened: 'm-opened',
                classesInitialized: 'm-initialized',
                classesStickyShowed: 'sticky-showed',
                classesStickyPanel: 'm-sticky_panel',
                isAlwaysVisible: false,
                ...super.prefs()
            };
        }

        init() {
            const self = this.ref('self');

            self.addClass(this.prefs().classesInitialized);

            this.emit('initobserver');

            this.eventBus().on('product.addTocart.intersects.viewport', 'togglePanel');
        }

        /**
         * @description Toggle class on product sticky panel to show/hide it
         * @param isShown - boolean value, witch tell the function remove or add class
         */
        togglePanel(isShown: boolean) {
            const self = this.ref('self');
            const selfElement = self.get() as HTMLElement;
            const backToTop = document.querySelector<HTMLInputElement>('.b-back_to_top');

            self.toggleClass(this.prefs().classesOpened, isShown);
            document.body.classList.toggle(this.prefs().classesStickyPanel, isShown);
            document.documentElement.style.setProperty('--sticky-element-height', `-${selfElement.offsetHeight}px`);

            // fix back to top button animations effects during toggling sticky panel
            if (backToTop) {
                if (selfElement.classList.contains(this.prefs().classesOpened)) {
                    clearTimeout(this.backToTopTimeout);
                    backToTop.classList.add(this.prefs().classesStickyShowed);
                } else {
                    this.backToTopTimeout = setTimeout(() => {
                        backToTop.classList.remove(this.prefs().classesStickyShowed);
                    }, 500);
                }
            }
        }
    }

    return CoreProductStickyPanel;
}

export type TCoreProductStickyPanel = ReturnType<typeof CoreProductStickyPanelClassCreator>;

export type TCoreProductStickyPanelInstance = InstanceType<TCoreProductStickyPanel>;

export default CoreProductStickyPanelClassCreator;
